import React, { lazy, Suspense } from 'react'
import { ElementType } from 'react'

import type { Props as RegisterFormProps } from './RegisterForm'
import type { Props as LoginButtonProps } from './LoginButton'
import type { Props as LoginFormProps } from './LoginForm'
import LoginButton from './LoginButton'
import Loader from 'content-components/_Primatives/Loader'
import Popup from '../00-Primitives/Popup'
import Paragraph from 'content-components/_Primatives/Paragraph'
import style from './style.scss'

const LoginPromise = import(
  /* webpackPrefetch: true */

  /* webpackChunkName: 'LoginForm' */
  './LoginForm'
)
const LoadableLogin = lazy(() => LoginPromise)

const LoadableRegister = lazy(
  () =>
    import(
      /* webpackChunkName: 'RegisterForm' */
      './RegisterForm'
    ),
)

export type Props = {
  className?: string
  closeLabelText?: string
  loginButton: Omit<LoginButtonProps, 'isExpanded' | 'onClick'>
  loginForm: Omit<LoginFormProps, 'onSuccess' | 'onToggleActive'> & {
    successDescriptionText?: string
    successTitleText?: string
    titleText: string
  }
  registerForm: Omit<RegisterFormProps, 'onSuccess' | 'onToggleActive'> & {
    successDescriptionText?: string
    successTitleText?: string
    titleText: string
  }
}

export type FormState = 'create' | 'login'

type State = {
  active: FormState
  FormComponent: ElementType
  open: boolean
  propsKey: 'loginForm' | 'registerForm'
  showSuccess: boolean
}

const forms = {
  login: {
    FormComponent: LoadableLogin,
    propsKey: 'loginForm' as const,
  },
  create: {
    FormComponent: LoadableRegister,
    propsKey: 'registerForm' as const,
  },
}

class Login extends React.PureComponent<Props, State> {
  static defaultProps = {
    className: '',
    closeLabelText: 'Close login modal',
  }

  state = {
    FormComponent: forms.login.FormComponent,
    propsKey: forms.login.propsKey,
    active: 'login' as const,
    open: false,
    showSuccess: false,
  }

  handleFormOpen = () =>
    this.setState((state) => ({
      showSuccess: false,
      open: !state.open,
      ...forms.login,
    }))

  handleToggleExpanded = (active: FormState) =>
    this.setState({
      active,
      showSuccess: false,
      ...forms[active],
    })

  handleSuccess = () => {
    const { propsKey } = this.state
    // eslint-disable-next-line react/prop-types
    const { successDescriptionText } = this.props[propsKey]

    this.setState({
      showSuccess: !!successDescriptionText,
      open: !!successDescriptionText,
    })
  }

  render() {
    const { closeLabelText, loginButton } = this.props
    const { open, FormComponent, propsKey, showSuccess } = this.state
    const {
      titleText,
      successDescriptionText,
      successTitleText,
      ...formProps
    } = this.props[propsKey]

    return (
      <>
        <LoginButton
          {...loginButton}
          isExpanded={open}
          onClick={this.handleFormOpen}
        />
        {open && (
          <Popup
            className={style.popup}
            closeLabelText={closeLabelText}
            titleText={
              showSuccess && successTitleText ? successTitleText : titleText
            }
            onClose={this.handleFormOpen}
          >
            {showSuccess && successDescriptionText ? (
              <Paragraph className={style.successDescriptionText}>
                {successDescriptionText}
              </Paragraph>
            ) : (
              <Suspense fallback={<Loader dark />}>
                <FormComponent
                  {...formProps}
                  onSuccess={this.handleSuccess}
                  onToggleActive={this.handleToggleExpanded}
                />
              </Suspense>
            )}
          </Popup>
        )}
      </>
    )
  }
}

export default Login
